<template>
  <b-container fluid>
    <esg-base-card title="데이터 업로드 / 다운로드"
                   :footer="false">

      <loading :active="loading" :z-index="1000"
               :is-full-page="false"/>

      <b-form @submit.stop.prevent
              class="my-3">
        <b-form-group>
          <div>1 단계: 수행하고자 하는 작업을 선택해주세요.</div>
          <b-form-radio-group
              id="type"
              name="type"
              class="mt-3"
              v-model="type"
              v-validate="{ required: true }"
              :state="validateState('type')"
              :options="types">
            <b-form-invalid-feedback id="type-feedback"
                                     :state="validateState('type')">
              수행하고자 하는 작업을 선택해주세요.
            </b-form-invalid-feedback>
          </b-form-radio-group>
        </b-form-group>

        <b-form-group class="mt-5">
          <div>2 단계: 대상 테이블을 선택해주세요.</div>
          <b-form-select id="table"
                         name="table"
                         type="text"
                         class="mt-3 w-50"
                         v-model="table.name"
                         v-validate="{ required: true }"
                         :options="tableOptions"
                         :state="validateState('table')">
          </b-form-select>
          <b-form-invalid-feedback id="table-feedback">
            테이블을 선택해주세요
          </b-form-invalid-feedback>
        </b-form-group>

        <div v-show="type && type !== 'download'">
          <b-form-group class="mt-5">
            <div>
              3 단계: 데이터 업로드를 위한 템플릿을 다운로드해주세요.
              <esg-base-info text="1행은 필드의 한글명, 2행은 필드의 물리명입니다.<br>템플릿의 3행부터 데이터를 입력해주세요."/>
            </div>
            <esg-base-button class="mt-3"
                             @click="download">템플릿 다운로드
            </esg-base-button>
          </b-form-group>

          <b-form-group class="mt-5">
            <div class="mb-2">4 단계: 작성하신 데이터를 업로드해주세요.</div>
            <b-form-file id="file"
                         name="file"
                         class="w-50"
                         placeholder="선택된 파일이 없습니다"
                         v-validate="{ required: true }"
                         :state="validateState('file')"
                         v-model="table.file"></b-form-file>
            <b-form-invalid-feedback id="file-feedback">
              파일을 업로드해주세요.
            </b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group class="mt-5">
          <esg-base-button variant="primary"
                           @click="onSubmit"
                           style="width: 150px">완료
          </esg-base-button>
        </b-form-group>
      </b-form>
    </esg-base-card>

    <esg-base-dashboard-modal id="esg-upload-result"
                              size="lg">
      <div class="error">업로드에 실패했습니다. 아래 행 번호와 오류 메시지를 참고하여 데이터를 수정해주세요.</div>
      <esg-table-without-meta :fields="resultFields"
                              :items="uploadErrors"
                              size="sm"
                              max-height="70vh"/>
    </esg-base-dashboard-modal>
  </b-container>
</template>

<script>
import EsgBaseCard from "@/components/esg/base/EsgBaseCard.vue";
import EsgBaseButton from "@/components/esg/base/EsgBaseButton.vue";
import EsgBaseInfo from "@/components/esg/base/EsgBaseInfo.vue";
import EsgTableWithoutMeta from "@/components/esg/base/EsgTableWithoutMeta.vue";
import EsgBaseDashboardModal from "@/components/esg/base/EsgBaseDashboardModal.vue";

export default {
  components: {EsgTableWithoutMeta, EsgBaseCard, EsgBaseButton, EsgBaseInfo, EsgBaseDashboardModal},
  async mounted() {
    let response = await axios.get(`/esg/lca/table-info`, {
      headers: {Authorization: accessToken()}
    });
    this.tableOptions = response.data.map(tableInfo => {
      return {
        value: tableInfo.name,
        text: tableInfo.label
      }
    });
    this.tableOptions.sort((a, b) => a.text.localeCompare(b.text));
  },
  data() {
    return {
      loading: false,
      type: null,
      types: [
        {text: "신규 데이터 생성", value: "insert"},
        {text: "기존 데이터 업데이트", value: "update"},
        {text: "기존 데이터 다운로드", value: "download"},
      ],
      table: {
        name: null,
        file: null,
        meta: null,
      },
      tableOptions: null,
      showMetaInfo: false,
      uploadErrors: [],
      resultFields: [
        {
          label: '행 번호',
          name: 'number',
          list: true,
          sortable: true,
        },
        {
          label: '오류 메시지',
          name: 'message',
          list: true,
          sortable: true,
        }
      ]
    }
  },
  methods: {
    async onSubmit() {
      if (this.type === 'insert') this.insert();
      else if (this.type === 'update') this.update();
      else if (this.type === 'download') this.download();
    },
    async insert() {
      try {
        this.loading = true;

        let result = await this.$validator.validate();
        if (!result) return;

        // Create formData
        let params = new FormData();
        params.append('name', this.table.name);
        params.append('file', this.table.file);

        // Submit
        let response = await axios.post('/esg/lca/tables', params, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: accessToken()
          }
        });

        this.loading = false;

        // If there are errors that occurred when inserting data, show them
        let errors = response.data.error.map(err => JSON.parse(err));
        if (errors.length > 0) {
          this.uploadErrors = errors;
          this.$bvModal.show('esg-upload-result');
        } else {
          await this.showMessage('업로드되었습니다.')
        }

        this.reset();
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async update() {
      try {
        this.loading = true;

        let result = await this.$validator.validate();
        if (!result) return;

        // Create formData
        let params = new FormData();
        params.append('name', this.table.name);
        params.append('file', this.table.file);

        // Submit
        let response = await axios.patch('/esg/lca/tables', params, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
            Authorization: accessToken()
          }
        });

        // If there are errors that occurred when inserting data, show them
        let errors = response.data.error.map(err => JSON.parse(err));
        if (errors.length > 0) {
          this.uploadErrors = errors;
          this.$bvModal.show('esg-upload-result');
        } else {
          await this.showMessage('업로드되었습니다.')
        }

        this.reset();
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async download() {
      try {
        this.loading = true;

        await this.$validator.reset();
        let result = await this.$validator.validate('table');
        if (!result) return;

        // Get Excel file
        let response = await axios.get(`/esg/lca/tables/${this.table.name}/download`, {
          responseType: 'blob',
          headers: {'Authorization': accessToken()},
        });

        // Create a URL for the file and download it
        const url = URL.createObjectURL(
            new Blob([response.data], {type: response.headers["content-type"]})
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${this.table.name}.xlsx`);
        link.click();

        this.reset();
      } catch (error) {
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.table.name = null;
      this.table.file = null;
      this.$validator.reset();
    },
    validateState(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.errors.has(ref);
      }
      return null;
    },
  }
}
</script>

<style scoped>
* {
  font-family: NanumGothic;
}

>>> .custom-control-input {
  z-index: 0 !important;
  opacity: 1;
  top: 2px;
}


/* Radio button style */
>>> .custom-control-input ~ .custom-control-label,
>>> .custom-control-input.is-valid ~ .custom-control-label {
  color: #000;
}

>>> .custom-control-input ~ .custom-control-label::before,
>>> .custom-control-input.is-valid ~ .custom-control-label::before,
>>> .custom-control-input ~ .custom-control-label::after,
>>> .custom-control-input.is-valid ~ .custom-control-label::after {
  display: none !important;
}

/* Select box style */
>>> .custom-select {
  padding: 0.3rem 2.5rem 0.3rem .8rem;
  height: 2rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #F6F6FA;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 8px) 5px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: NanumGothic;
  color: #2E2E38;
  font-size: .875rem;
  font-weight: 400;
}

>>> .custom-select:focus {
  border-color: unset !important;
  box-shadow: unset !important;
}

>>> .custom-select.is-valid {
  border-color: unset;
  box-shadow: unset;
  background-image: none;
}

/* File input style */
>>> .custom-file-input ~ .custom-file-label {
  border-color: unset !important;
  box-shadow: unset !important;
}

>>> .custom-file-label::after {
  content: '파일 선택' !important;
}

>>> .custom-file-input:focus ~ .custom-file-label {
  border-color: unset;
  box-shadow: unset;
}

.error {
  color: #FF4560;
  font-size: 1.125rem;
  font-weight: 500;
  margin-bottom: 1rem;
}
</style>